import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { get } from 'lodash';
import {
  Box,
  Checkbox,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon, Clear as ClearIcon } from '@mui/icons-material';
import BaseMUIDataTable from './BaseMUIDataTable/BaseMUIDataTable';
import TableActionButton from './TableActionButton';
import TableActionsButtonsWrapper from './TableActionsButtonsWrapper';
import { getMuiTableDataIndex } from '../helpers/getters';
import { getUserFullName } from '../helpers/user';
import { isUndefinedOrNull } from '../helpers';
import { userRegistrationFilterOptions } from '../helpers/MuiTableCustomFilters';
import { formatPhoneNumber, generateCustomCsvFileName } from '../helpers/formatters';
import MinMaxTimePicker from './MinMaxTimePicker';
import useActiveUser from '../hooks/useActiveUser';

const receivingDaysFilterOptions = (setReceivingDaysFilterValue) => ({
  logic: (days, filters) => {
    if (filters.length) {
      return !filters.every((filter) => (days || []).find((d) => d === filter));
    }

    return false;
  },
  display: (filterList, onChange, index, column) => {
    const optionValues = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    return (
      <FormControl variant="standard">
        <InputLabel htmlFor="select-multiple-chip">Receiving Days</InputLabel>
        <Select
          variant="standard"
          multiple
          value={filterList[index]}
          renderValue={(selected) => selected.join(', ')}
          onChange={(event) => {
            setReceivingDaysFilterValue(event.target.value);
            filterList[index] = event.target.value;
            onChange(filterList[index], index, column);
          } }
        >
          {optionValues.map((item) => (
            <MenuItem key={item} value={item}>
              <Checkbox color="primary" checked={filterList[index].indexOf(item) > -1} />
              <ListItemText primary={item} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  },
});

const receivingHoursFilterOptions = (
  afterFilterValue,
  setAfterFilterValue,
  beforeFilterValue,
  setBeforeFilterValue,
  receivingDaysFilterValue
) => ({
  logic: (value, filters) => {
    if (!filters || filters.length === 0) {
      return false;
    }

    const [after, before] = filters;
    const daysMap = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    if (!after && !before) {
      return false;
    }

    let afterfilter = false;
    let beforeFilter = false;

    if (after) {
      if (receivingDaysFilterValue?.length) {
        const selectedDaysHoursFound = receivingDaysFilterValue.every((day) =>
          (value || [])
            .filter((wrh) => daysMap[wrh.day_of_week] === day)
            .find(
              (wrh) =>
                moment(wrh.start, 'HH:mm:ss').isSameOrAfter(moment(after, 'hh:mm a')) ||
                moment(after, 'hh:mm a').isBefore(moment(wrh.end, 'HH:mm:ss'))
            )
        );

        // return !selectedDaysHoursFound;
        afterfilter = selectedDaysHoursFound;
      } else {
        const allDaysHoursFound = (value || []).find(
          (wrh) =>
            moment(wrh.start, 'HH:mm:ss').isSameOrAfter(moment(after, 'hh:mm a')) ||
            moment(after, 'hh:mm a').isBefore(moment(wrh.end, 'HH:mm:ss'))
        );

        // return !allDaysHoursFound;
        afterfilter = allDaysHoursFound;
      }
    }

    if (before) {
      if (receivingDaysFilterValue?.length) {
        const selectedDaysHoursFound = receivingDaysFilterValue.every((day) =>
          (value || []).filter((wrh) => daysMap[wrh.day_of_week] === day)
            .find((wrh) =>
                moment(moment(wrh.end, 'HH:mm:ss'), 'HH:mm:ss').isBefore(moment(before, 'hh:mm a')) ||
                moment(moment(wrh.start, 'HH:mm:ss'), 'HH:mm:ss').isBefore(moment(before, 'hh:mm a'))
            )
        );

        // return !selectedDaysHoursFound;
        beforeFilter = selectedDaysHoursFound;
      } else {
        const allDaysHoursFound = (value || []).find(
          (wrh) =>
            moment(wrh.end, 'HH:mm:ss').isBefore(moment(before, 'hh:mm a')) ||
            moment(wrh.start, 'HH:mm:ss').isBefore(moment(before, 'hh:mm a'))
        );

        // return !allDaysHoursFound;
        beforeFilter = allDaysHoursFound;
      }
    }

    if (after && !before) return !afterfilter;
    if (!after && before) return !beforeFilter;
    if (after && before) return !(afterfilter && beforeFilter);

    return false;
  },
  display: (filterList, onChange, index, column) => {
    const handleAfterTimeChange = ({ event, isValid }) => {
      if (isValid) {
        setAfterFilterValue(event.format('hh:mm a'));
      }
    };

    const handleAfterTimeUpdate = () => {
      filterList[index][0] = afterFilterValue;
      onChange(filterList[index], index, column);
    };

    const clearAfter = () => {
      filterList[index][0] = null;
      onChange(filterList[index], index, column);
      setAfterFilterValue(null);
    };

    const handleBeforeTimeChange = ({ event, isValid }) => {
      if (isValid) {
        setBeforeFilterValue(event.format('hh:mm a'));
      }
    };

    const handleBeforeTimeUpdate = () => {
      filterList[index][1] = beforeFilterValue;
      onChange(filterList[index], index, column);
    };

    const clearBefore = () => {
      filterList[index][1] = null;
      onChange(filterList[index], index, column);
      setBeforeFilterValue(null);
    };

    return (
      <FormControl variant="standard">
        <Box mb={1}>
          <Typography variant="body2">Receiving Hours</Typography>
        </Box>
        <Box display="flex">
          <Box display="flex">
            <MinMaxTimePicker
              inputVariant="outlined"
              size="small"
              label="After"
              value={filterList[index][0] ? moment(filterList[index][0], 'hh:mm a') : null}
              maxTime={
                beforeFilterValue
                  ? moment(beforeFilterValue, 'hh:mm a')
                  : moment().set({ hour: '23', minute: '55', second: '00' })
              }
              onTimeChange={handleAfterTimeChange}
              onClose={handleAfterTimeUpdate}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <IconButton size="small" onClick={clearAfter}>
              <ClearIcon size="small" />
            </IconButton>
          </Box>

          <Box display="flex">
            <MinMaxTimePicker
              inputVariant="outlined"
              size="small"
              label="Before"
              value={filterList[index][1] ? moment(filterList[index][1], 'hh:mm a') : null}
              minTime={
                afterFilterValue
                  ? moment(afterFilterValue, 'hh:mm a')
                  : moment().set({ hour: '00', minute: '05', second: '00' })
              }
              onTimeChange={handleBeforeTimeChange}
              onClose={handleBeforeTimeUpdate}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <IconButton size="small" onClick={clearBefore}>
              <ClearIcon />
            </IconButton>
          </Box>
        </Box>
      </FormControl>
    );
  },
});

const ReceiversTable = ({
  receiversList,
  locationsList = [],
  onRowClick,
  onPrimaryContactUserClick,
  tableId,
  siteName,
  customToolbar = () => null,
  onDeleteClick,
  isAdmin,
  isLoading,
  siteRescues = [],
}) => {
  const activeUser = useActiveUser();
  const tableOptions = useSelector((state) => state.tableOptions);
  const [afterFilterValue, setAfterFilterValue] = useState();
  const [beforeFilterValue, setBeforeFilterValue] = useState();
  const [receivingDaysFilterValue, setReceivingDaysFilterValue] = useState();

  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const receiversListMapped = (receiversList || []).map((receiver) => ({
    ...receiver,
    receiving_days: (receiver?.weekly_receiving_hours || []).reduce((acc, item) => {
      const day = days[item.day_of_week];
      if (!acc.includes(day)) {
        acc.push(day);
      }

      return acc;
    }, []),
  }));

  useEffect(() => {
    const previousTableState = get(tableOptions, [activeUser.id, tableId], null);
    const weeklyReceivingHourscolumnIndex = get(previousTableState, 'columns', []).findIndex(
      (c) => c.name === 'weekly_receiving_hours'
    );
    const receivingHoursFilter = get(previousTableState, ['filterList', weeklyReceivingHourscolumnIndex], null);

    const receivingDayscolumnIndex = get(previousTableState, 'columns', []).findIndex(
      (c) => c.name === 'receiving_days'
    );
    const receivingDaysFilter = get(previousTableState, ['filterList', receivingDayscolumnIndex], null);

    if (receivingHoursFilter?.length) {
      setAfterFilterValue(receivingHoursFilter[0]);
      setBeforeFilterValue(receivingHoursFilter[1]);
    }

    if (receivingDaysFilter?.length) {
      setReceivingDaysFilterValue(receivingDaysFilter);
    }
  }, []);

  const primaryContactCustomBodyRender = (value, tableMeta, csv = false) => {
    const primaryContactFirstName = receiversList[getMuiTableDataIndex(tableMeta)].primary_contact_firstname;
    const primaryContactLastName = receiversList[getMuiTableDataIndex(tableMeta)].primary_contact_lastname;

    if (!primaryContactFirstName && !primaryContactLastName) {
      return null;
    }

    const userFullName = getUserFullName({
      firstname: primaryContactFirstName,
      lastname: primaryContactLastName,
    });

    if (csv) {
      return userFullName;
    }

    return (
      <Link
        href="#"
        onClick={event => {
          event.preventDefault();
      
          return onPrimaryContactUserClick(receiversList[getMuiTableDataIndex(tableMeta)].primary_contact_id);
        }}
        underline="hover"
        // value set for search to work
        value={userFullName}
      >
        {userFullName}
      </Link>
    );
  };

  const primaryContactPhoneCustomBodyRender = (value, csv = false) => {
    if (!value) {
      return null;
    }

    const formattedPhoneNumber = formatPhoneNumber(value);

    if (csv) {
      return formattedPhoneNumber;
    }

    // value set for search to work
    return <Link href={`tel:${value}`} underline="hover" value={formattedPhoneNumber}>{formattedPhoneNumber}</Link>;
  };

  const activeCustomBodyRender = value => value === 1 ? 'Accepting Donations' : 'Not Accepting Donations';

  const createdAtCustomBodyRender = (value) =>
    !isUndefinedOrNull(value) ? moment(value, 'YYYY-MM-DD').format('MM/DD/YYYY') : null;

  const actionsCustomBodyRender = (tableMeta) => {
    const receiverId = tableMeta.rowData[0];
    const receiverName = tableMeta.rowData[1];
    const receivierRescues = siteRescues.filter((rescue) => rescue.receiver_id === receiverId);
    return (
      <TableActionsButtonsWrapper>
        <TableActionButton
          title="Edit Receiving Agency"
          onClick={() => onRowClick(tableMeta.rowData[0])}
          data-testid="edit-receiver-button"
        />
        {isAdmin && (
          <TableActionButton
            disabled={!!receivierRescues.length}
            title={
              receivierRescues.length
                ? `This Receiving Agency is associated with upcoming rescues and cannot be deleted. Please remove any upcoming rescues associated with this agency to continue.`
                : 'Delete Receiving Agency'
            }
            icon={DeleteIcon}
            onClick={() => onDeleteClick(tableMeta.rowData[0], receiverName)}
          />
        )}
      </TableActionsButtonsWrapper>
    );
  };

  const notesCustomBodyRender = (value, tableMeta, csv = false) => {
    const receiver = receiversList[getMuiTableDataIndex(tableMeta)];
    const location = locationsList.find((l) => l.id === receiver.location_id);

    return location?.notes || '-';
  };

  return (
    <BaseMUIDataTable
      isLoading={isLoading}
      tableId={tableId}
      title="Receiving Agencies"
      data={receiversListMapped}
      columns={[
        {
          name: 'id',
          label: 'ID',
          options: {
            filter: false,
            display: false,
          },
        },
        {
          name: 'name',
          label: 'Name',
        },
        {
          name: 'address1',
          label: 'Address',
        },
        {
          name: 'city',
          label: 'City',
          options: {
            filterType: 'multiselect',
          },
        },
        {
          name: 'st',
          label: 'State',
        },
        {
          name: 'zip',
          label: 'Zip Code',
        },
        {
          name: 'primary_contact',
          label: 'Contact',
          options: {
            customBodyRender: (value, tableMeta) => primaryContactCustomBodyRender(value, tableMeta),
            customBodyRenderCSV: (value, tableMeta) => primaryContactCustomBodyRender(value, tableMeta, true),
            filter: false,
          },
        },
        {
          name: 'primary_contact_email',
          label: 'Contact email',
          options: {
            filter: false,
            searchable: true,
            display: false,
            sort: false,
            customBodyRender: (value, tableMeta) => {
              const primaryContactEmail = receiversList[getMuiTableDataIndex(tableMeta)].primary_contact_email;

              if (!primaryContactEmail) {
                return null;
              }

              return (
                <Link href={`mailto:${primaryContactEmail}`} underline="hover">
                  {primaryContactEmail}
                </Link>
              );
            },
          },
        },
        {
          name: 'primary_contact_phone',
          label: 'Contact phone',
          options: {
            filter: false,
            searchable: true,
            display: false,
            sort: false,
            customBodyRender: value => primaryContactPhoneCustomBodyRender(value),
            customBodyRenderCSV: value => primaryContactPhoneCustomBodyRender(value, true),
          },
        },
        {
          name: 'active',
          label: 'Active',
          options: {
            display: false,
            filterList: ['Accepting Donations'],
            filterOptions: {
              names: ['Accepting Donations', 'Not Accepting Donations']
            },
            customBodyRender: value => activeCustomBodyRender(value),
            customBodyRenderCSV: value => activeCustomBodyRender(value),
          },
        },
        {
          name: 'created_at',
          label: 'Completed Registration',
          options: {
            filter: true,
            display: false,
            customBodyRender: value => createdAtCustomBodyRender(value),
            customBodyRenderCSV: value => createdAtCustomBodyRender(value),
            ...userRegistrationFilterOptions,
          },
        },
        {
          name: 'receiving_days',
          label: 'Receiving Days',
          options: {
            filter: true,
            display: false,
            forceDisplay: true,
            sort: false,
            viewColumns: false,
            customFilterListOptions: {
              render: (value) => `Receiving day(s): ${value}`,
              update: (filterList, filterPos, index) => {
                filterList[index] = [];
                setReceivingDaysFilterValue(null);

                return filterList;
              },
            },
            filterType: 'custom',
            filterOptions: receivingDaysFilterOptions(setReceivingDaysFilterValue),
          },
        },
        {
          name: 'weekly_receiving_hours',
          label: 'Receiving Hours',
          options: {
            filter: true,
            display: false,
            forceDisplay: true,
            sort: false,
            viewColumns: false,
            customFilterListOptions: {
              render: (value) => {
                const chips = [];

                if (value[0]) {
                  chips.push(`Receiving after: ${value[0]}`);
                }

                if (value[1]) {
                  chips.push(`Receiving before: ${value[1]}`);
                }

                return chips;
              },
              update: (filterList, filterPos, index) => {
                // Removing 'after' chip sets 'before' filterPos to negative value.
                // Use Math.abs() to convert filterPos to positive value.
                filterList[index][Math.abs(filterPos)] = null;

                if (filterPos === 0) {
                  setAfterFilterValue(null);
                }

                if (Math.abs(filterPos) === 1) {
                  setBeforeFilterValue(null);
                }

                return filterList;
              },
            },
            filterType: 'custom',
            filterOptions: receivingHoursFilterOptions(
              afterFilterValue,
              setAfterFilterValue,
              beforeFilterValue,
              setBeforeFilterValue,
              receivingDaysFilterValue
            ),
          },
        },
        {
          name: 'notes',
          label: 'Internal Location Notes',
          options: {
            filter: false,
            sort: false,
            display: true,
            customBodyRender: (value, tableMeta) => notesCustomBodyRender(value, tableMeta),
            customBodyRenderCSV: (value, tableMeta) => notesCustomBodyRender(value, tableMeta, true),
          },
        },
        {
          name: 'actions',
          label: 'Actions',
          options: {
            download: false,
            filter: false,
            sort: false,
            display: true,
            customBodyRender: (value, tableMeta) => actionsCustomBodyRender(tableMeta),
          },
        },
      ]}
      options={{
        customToolbar: customToolbar,
        responsive: 'simple',
        selectableRows: 'none',
        downloadOptions: {
          filename: generateCustomCsvFileName({
            baseFileName: siteName ? `${siteName} Receiving Agencies` : 'Receiving Agencies'
          }),
        },
      }}
    />
  );
};

export default ReceiversTable;
